
import React, { useState, useEffect, useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import { Globe } from "lucide-react";

import logo from "../../assets/Logo.webp";
import logotext from "../../assets/logo-text-only.webp";
import defaultAvatar from "../../assets/avatar.webp";
import "./LandingHeader.css";
import AuthContext from "../../AuthContext";
import Coins from "../Coins/Coins";
import ContextSwitcher from "../ui/ContextSwitcher";

const GET_USER_PROFILE = gql`
  query GetUserProfile($sessionId: String!) {
    getUserProfile(sessionId: $sessionId) {
      username
      avatarUrl
    }
  }
`;

export default function Header() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout: performLogout, coins, sessionId } =
    useContext(AuthContext);

  const [isScrolled, setIsScrolled] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const navigate = useNavigate();

  // scroll listener para el estilo scrolled
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // GraphQL user profile
  const { data, loading, error } = useQuery(GET_USER_PROFILE, {
    variables: { sessionId: sessionId || "" },
    skip: !sessionId,
    fetchPolicy: "network-only",
  });
  const userProfile = data?.getUserProfile || {};

  const handleLogout = (e) => {
    e.preventDefault();
    performLogout();
  };

  const toggleLangMenu = () => setLangMenuOpen((open) => !open);
  const selectLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setLangMenuOpen(false);
  };

  return (
    <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
      {/* Logo a la izquierda */}
      <div className="header-pill brand-pill">
        <NavLink to="/" className="brand-link">
          <img
            className="logo-header logo-icon"
            src={logo}
            alt={t("header.logoAlt")}
          />
          <img
            className="logo-header logo-text"
            src={logotext}
            alt={t("header.logoTextAlt")}
          />
        </NavLink>
      </div>

      {/* Context Switcher - Solo cuando está autenticado */}
      {isAuthenticated && (
        <div className="header-pill context-pill">
          <ContextSwitcher />
        </div>
      )}

      {/* Only User Pill on the right */}
      <div className="header-pill user-pill">
        {!isAuthenticated ? (
          <div className="not-logged-wrapper">

            <NavLink
              to="/Login"
              className="auth-btn launch-btn"
              onClick={(e) => {
                e.preventDefault();
                window.open("/Login", "_blank", "noopener,noreferrer");
              }}
            >
              {t("header.launchApp")}
            </NavLink>
            <div className="lang-wrapper">
              <button
                className="lang-btn"
                aria-label="Change language"
                onClick={toggleLangMenu}
              >
                <Globe size={20} />
              </button>
              {langMenuOpen && (
                <ul className="lang-menu">
                  <li onClick={() => selectLanguage("en")}>English</li>
                  <li onClick={() => selectLanguage("es")}>Español</li>
                </ul>
              )}
            </div>
          </div>
        ) : (
          <div className="profile-container">
            <div className="coins-and-avatar">
              <Coins coinsAmount={coins} />
              <img
                className="avatar-img"
                src={
                  userProfile.avatarUrl && !loading && !error
                    ? userProfile.avatarUrl
                    : defaultAvatar
                }
                alt={t("header.avatarAlt")}
                onError={(e) => {
                  e.target.src = defaultAvatar;
                }}
              />
            </div>
            <div className="dropdown-content">
              <NavLink to="/Profile">{t("header.viewProfile")}</NavLink>
              <a href="/" onClick={handleLogout}>
                {t("header.logOut")}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
